import { Button, Stack, styled } from '@mui/material'
import { faucet as apiFaucet } from 'api/v1/faucet'
import { Currency } from 'api/v1/market'
import Loading from 'components/Loading'
import toastify from 'components/Toastify'
import { ENABLE_GRECAPTCHA, GRECAPTCHA_KEY, SUB_ACCOUNT_ID } from 'config'
import { FaucetTokenSize, FaucetWhiteList } from 'constants/faucet'
import { memo, useMemo, useRef, useState } from 'react'
import ReCaptcha from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { updateFaucetQueueLen } from 'store/app/actions'
import { useCurrentNetwork } from 'store/app/hooks'
import { useSpotCurrencies } from 'store/market/hooks'
import { useCurrentThemeType } from 'store/settings/hooks'
import { FlexCenter } from 'styles'
import { FaucetTokenSelection } from './TokenSelection'
import { FaucetType } from './atoms'
import { FaucetTips, GrayWrap } from './styles'
import { updateModal } from '../../store/app/actions/modalActions'
import { useWeb3Address } from '../../store/web3/hooks'

const CaptchaWrap = styled(FlexCenter)`
  position: relative;
`

const CaptchaTip = styled(FlexCenter)`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: ${(props) => props.theme.color?.text70};
  background: ${(props) => props.theme.color?.bg};
`

export const FaucetLayer2 = memo(() => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const currentNetwork = useCurrentNetwork()
  const recaptchaRef = useRef<any>(null)
  const theme = useCurrentThemeType()
  const [showCaptchaErrorTips, setShowCaptchaErrorTips] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [claiming, setClaiming] = useState(false)
  const [selectedToken, setSelectedToken] = useState<Currency>()
  const address = useWeb3Address()
  const tokenList = useSpotCurrencies()

  const faucetTokenList = useMemo(() => {
    const _whiteList = FaucetWhiteList.map((item) => item.toLocaleUpperCase())
    return tokenList.filter(
      (item) =>
        item?.name && _whiteList.includes(item?.name.toLocaleUpperCase()),
    )
  }, [tokenList])

  const onHandleClaim = async () => {
    if (!selectedToken?.name) {
      return
    }
    if (claiming) {
      return
    }

    if (ENABLE_GRECAPTCHA && !captchaToken) {
      toastify.error('Please complete the reCAPTCHA.')
      return
    }

    setClaiming(true)

    try {
      const res = await apiFaucet({
        type: FaucetType.Layer2,
        receiveAddress: address,
        symbol: selectedToken.name!,
        amount: FaucetTokenSize[selectedToken.name!],
        captchaToken: captchaToken!,
        chainId: currentNetwork.layerTwoChainId!,
        subAccountId: SUB_ACCOUNT_ID,
      })
      const { data } = res
      if (data.code === 0) {
        dispatch(updateFaucetQueueLen(data?.data?.queue ?? 0))
        dispatch(updateModal({ modal: 'depositing', open: true }))
        dispatch(updateModal({ modal: 'faucet', open: false }))
      } else {
        toastify.error(data?.message)
      }
    } catch (e) {
      toastify.error('Request failed')
    } finally {
      setClaiming(false)

      setCaptchaToken('')
      try {
        recaptchaRef?.current?.reset()
      } catch (e) {}
    }
  }
  return (
    <Stack spacing={2}>
      <GrayWrap spacing={1}>
        <FaucetTips>
          Faucet tokens will be deposited into your <strong>ZKEX</strong>{' '}
          wallet. (~30 sec)
        </FaucetTips>
        <FaucetTokenSelection
          tokenList={faucetTokenList}
          selectedToken={selectedToken!}
          onChangeToken={(token) => {
            setSelectedToken(token)
          }}
        />
      </GrayWrap>
      {ENABLE_GRECAPTCHA ? (
        <CaptchaWrap>
          <ReCaptcha
            ref={recaptchaRef}
            sitekey={GRECAPTCHA_KEY}
            theme={theme}
            onChange={(t: string) => {
              if (t) {
                setCaptchaToken(t)
              }
            }}
            onErrored={() => {
              setShowCaptchaErrorTips(true)
            }}
          />
          <CaptchaTip sx={{ display: showCaptchaErrorTips ? 'flex' : 'none' }}>
            {t('captcha-tip', {
              defaultValue: 'reCAPTCHA failed to load. Please try again later.',
            })}
          </CaptchaTip>
        </CaptchaWrap>
      ) : null}
      <Button
        variant="contained"
        fullWidth={true}
        className={selectedToken?.name && !claiming ? 'active' : 'disable'}
        onClick={() => onHandleClaim()}
      >
        {claiming ? <Loading /> : null}
        <span>
          {claiming ? 'claiming' : t('faucet-claim', { defaultValue: 'Claim' })}
        </span>
      </Button>
    </Stack>
  )
})
